import { cs } from '../../lib/currency';
/**
 * EnrollmentForecastMatrix — read-only projected billing across 1-6
 * months for every active/approved/paused enrollment.
 *
 * NEVER creates invoices, calls Stripe, or consumes credits. All state
 * comes from GET /api/admin/enrollments/forecast.
 *
 * Feb 2026.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { toast } from 'sonner';
import { Loader2, ArrowUpDown, ShieldAlert, Info, Eye, CalendarX2, Search } from 'lucide-react';
import api from '../../services/api';

const STATE_TONE = {
  active:                   'bg-emerald-100 text-emerald-700 border-emerald-200',
  paused:                   'bg-amber-100 text-amber-800 border-amber-200',
  scheduled_cancel:         'bg-rose-100 text-rose-700 border-rose-200',
  not_yet_started:          'bg-sky-100 text-sky-700 border-sky-200',
  covered_by_invoice:       'bg-slate-100 text-slate-600 border-slate-200',
  cycle_not_due_this_month: 'bg-slate-100 text-slate-500 border-slate-200',
  zero_fee:                 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATE_LABEL = {
  active:                   'Active',
  paused:                   'Paused',
  scheduled_cancel:         'Cancelled',
  not_yet_started:          'Not started',
  covered_by_invoice:       'Paid',
  cycle_not_due_this_month: 'Cycle covers',
  zero_fee:                 'Zero fee',
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// iter325b — Human-friendly cancel-date formatter. Renders "Jul 31, 2026"
// from an ISO "2026-07-31". Returns null if the input is falsy so
// the caller can render an unadorned fallback tooltip.
function formatCancelDateHuman(iso) {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.valueOf())) return iso;
  return d.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/**
 * Renders the state badge for a forecast cell. When the state is
 * `scheduled_cancel`, wraps the badge in a Tooltip that surfaces the
 * cancellation-effective date without the admin having to click into
 * the enrollment. Native browser `title` is preserved as a fallback
 * (touch devices / narrator).
 */
function StateBadge({ cell }) {
  const label = STATE_LABEL[cell.state] || cell.state;
  const tone = STATE_TONE[cell.state] || '';
  const badge = (
    <Badge
      variant="outline"
      className={`text-[9px] px-1 py-0 ${tone} ${cell.state === 'scheduled_cancel' ? 'cursor-help' : ''}`}
      title={cell.explanation || ''}
      data-testid={`forecast-state-badge-${cell.state}`}
    >
      {label}
    </Badge>
  );

  // iter334c v3-v4 (Aug 2026) — When the state is `covered` OR
  // `cycle_not_due_this_month` (both mean: cycle already has a paid
  // invoice) AND the enrollment's cancel effective date lands in
  // THIS month (Dheeraj biannual-paid + cancel-eff 8/31), render a
  // secondary "Cancelled" pill next to the primary pill. The
  // primary state stays as-is (so the dollar amount reads $0, not
  // the misleading $1800), but the admin still sees "Cancelled".
  const covered_and_cancelled = (
    (cell.state === 'covered' || cell.state === 'cycle_not_due_this_month')
    && !!cell.cancel_effective_date
    && cell.cancel_effective_date.slice(0, 7) === (cell.target_month || cell.month || '')
  );

  if (cell.state !== 'scheduled_cancel' && !covered_and_cancelled) return badge;

  const humanDate = formatCancelDateHuman(cell.cancel_effective_date);
  const secondaryTag = covered_and_cancelled ? (
    <Badge
      key="also-cancelled"
      variant="outline"
      className={`text-[9px] px-1 py-0 ml-1 ${STATE_TONE.scheduled_cancel} cursor-help`}
      title={humanDate ? `Cancelled effective ${humanDate}` : 'Cancellation scheduled'}
      data-testid="forecast-covered-and-cancelled-badge"
    >
      {STATE_LABEL.scheduled_cancel}
    </Badge>
  ) : null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid="forecast-cancelled-badge">
            {badge}
            {secondaryTag}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="bg-slate-900 text-white text-[11px] max-w-xs"
          data-testid="forecast-cancelled-tooltip"
        >
          <div className="flex items-start gap-1.5">
            <CalendarX2 className="h-3 w-3 mt-0.5 text-rose-300 shrink-0" />
            <div>
              <div className="font-semibold">
                {humanDate ? `Cancelled effective ${humanDate}` : 'Cancellation scheduled'}
              </div>
              <div className="text-slate-300 mt-0.5">
                {covered_and_cancelled
                  ? 'This month is already paid via a prior invoice. This is the last cycle before cancellation.'
                  : 'This is the last billed cycle. Later months are hidden from the forecast.'}
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const fmt = (n) => (n == null ? '—' : `${cs()}${Number(n).toFixed(2)}`);

/**
 * Reduce enrollment×month row list into a single row per enrollment
 * with columns keyed by month. Preserves labels from the row.
 */
function buildMatrix(rows, months) {
  const byEnr = new Map();
  for (const r of rows) {
    const key = r.enrollment_id;
    if (!byEnr.has(key)) {
      byEnr.set(key, {
        enrollment_id: r.enrollment_id,
        player_id: r.player_id,
        player_name: r.player_name,
        parent_id: r.parent_id,
        parent_name: r.parent_name,
        parent_email: r.parent_email,
        program_name: r.program_name,
        class_name: r.class_name,
        location_name: r.location_name,
        payment_frequency: r.payment_frequency,
        enrollment_status: r.enrollment_status,
        next_billing_date: r.next_billing_date,
        months: {},
        total: 0,
      });
    }
    const row = byEnr.get(key);
    row.months[r.target_month] = r;
    row.total = (row.total || 0) + Number(r.net_after_credit || 0);
  }
  // Also keep the total by-month
  return Array.from(byEnr.values());
}

export default function EnrollmentForecastMatrix() {
  const [startMonth, setStartMonth] = useState(currentMonth());
  const [months, setMonths] = useState(6);
  const [frequency, setFrequency] = useState('all');
  // iter333 — Location filter (owner-requested). Populated lazily from
  // /api/locations/active. Empty string == "all locations".
  const [locationId, setLocationId] = useState('all');
  const [locations, setLocations] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sortBy, setSortBy] = useState('parent_name');
  const [sortDir, setSortDir] = useState('asc');
  const [stateFilter, setStateFilter] = useState([]); // [] = all states

  // iter328 — Credit-divergence Inspect modal state.
  const [inspectOpen, setInspectOpen] = useState(false);
  const [inspectAnomaly, setInspectAnomaly] = useState(null);
  const [inspectData, setInspectData] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [resolveDirection, setResolveDirection] = useState('skip');
  const [resolveReason, setResolveReason] = useState('');
  const [resolving, setResolving] = useState(false);

  const openInspect = async (anomaly) => {
    if (!anomaly?.parent_id) return;
    setInspectAnomaly(anomaly);
    setInspectOpen(true);
    setInspectData(null);
    setResolveDirection('skip');
    setResolveReason('');
    setInspectLoading(true);
    try {
      const { data: d } = await api.get(
        `/admin/billing/credit-divergence-inspect/${anomaly.parent_id}?limit=200`
      );
      setInspectData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load ledger history');
    } finally {
      setInspectLoading(false);
    }
  };

  const submitResolve = async () => {
    if (!inspectAnomaly?.parent_id) return;
    if (resolveDirection !== 'skip' && !resolveReason.trim()) {
      toast.error('Reason is required when writing a resolution.');
      return;
    }
    setResolving(true);
    try {
      const { data: d } = await api.post(
        `/admin/billing/credit-divergence-resolve/${inspectAnomaly.parent_id}` +
        `?direction=${resolveDirection}&confirm=true`,
        resolveReason.trim(),
        { headers: { 'Content-Type': 'application/json' } }
      );
      toast.success(
        resolveDirection === 'skip'
          ? 'Divergence acknowledged — no changes made'
          : resolveDirection === 'balance_wins'
            ? `Wrote compensating ledger row (Δ ${fmt(d.planned_delta ?? d.diff)})`
            : `Updated stored balance to ${fmt(d.planned_new_stored_balance ?? d.ledger_sum)}`
      );
      setInspectOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resolve failed');
    } finally {
      setResolving(false);
    }
  };

  // iter333 — Fetch active locations once for the Location filter.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/locations/active');
        if (!cancelled) setLocations(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setLocations([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({
          start_month: startMonth,
          months: String(months),
        });
        if (frequency !== 'all') qs.set('frequency', frequency);
        if (locationId && locationId !== 'all') qs.set('location_id', locationId);
        const res = await api.get(`/admin/enrollments/forecast?${qs}`);
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || 'Failed to load forecast');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [startMonth, months, frequency, locationId]);

  const monthsList = data?.months || [];
  const totalsByMonth = data?.totals_by_month || {};
  const anomalies = data?.anomalies || [];

  const matrix = useMemo(() => {
    if (!data?.rows) return [];
    let m = buildMatrix(data.rows, monthsList);
    if (stateFilter.length > 0) {
      m = m.filter(row => Object.values(row.months).some(x => stateFilter.includes(x.state)));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    m.sort((a, b) => {
      const va = a[sortBy] ?? '';
      const vb = b[sortBy] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return m;
  }, [data, monthsList, sortBy, sortDir, stateFilter]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const renderSortHeader = (col, children, testid) => (
    <button
      onClick={() => toggleSort(col)}
      data-testid={testid}
      className="inline-flex items-center gap-1 font-semibold hover:text-slate-900"
    >
      {children}
      <ArrowUpDown className={`h-3 w-3 ${sortBy === col ? 'text-emerald-600' : 'text-slate-400'}`} />
    </button>
  );

  const totalNet = useMemo(() =>
    matrix.reduce((acc, r) => acc + (r.total || 0), 0)
  , [matrix]);

  return (
    <div className="space-y-4" data-testid="forecast-tab-root">
      {/* Read-only banner */}
      <Alert className="border-sky-200 bg-sky-50" data-testid="forecast-readonly-banner">
        <Eye className="h-4 w-4 text-sky-700" />
        <AlertTitle className="text-sky-800">Projection only — read-only</AlertTitle>
        <AlertDescription className="text-sky-700 text-xs">
          Nothing on this tab creates invoices, charges cards, or consumes
          credits. Numbers show what <em>would</em> bill based on current
          contracts, next billing dates, existing invoices, and credit
          balances. Actual invoices are still generated by the billing cron
          on their scheduled date.
        </AlertDescription>
      </Alert>

      {/* Filters */}
      <Card>
        <CardContent className="pt-5">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Start month</Label>
              <Input
                type="month"
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value || currentMonth())}
                data-testid="forecast-start-month"
              />
            </div>
            <div>
              <Label className="text-xs">Projection window</Label>
              <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
                <SelectTrigger data-testid="forecast-months-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 months</SelectItem>
                  <SelectItem value="4">4 months</SelectItem>
                  <SelectItem value="5">5 months</SelectItem>
                  <SelectItem value="6">6 months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* iter333 — Location filter */}
            <div>
              <Label className="text-xs">Location</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger data-testid="forecast-location-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="forecast-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="biannual">Biannual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">State filter</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-between font-normal h-9"
                    data-testid="forecast-state-filter"
                  >
                    <span className="truncate">
                      {stateFilter.length === 0
                        ? 'All states'
                        : stateFilter.length === 1
                          ? (STATE_LABEL[stateFilter[0]] || stateFilter[0])
                          : `${stateFilter.length} selected`}
                    </span>
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-50 ml-1 shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuCheckboxItem
                    checked={stateFilter.length === 0}
                    onCheckedChange={() => setStateFilter([])}
                    onSelect={(e) => e.preventDefault()}
                    data-testid="forecast-state-filter-all"
                  >
                    All states
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  {Object.entries(STATE_LABEL).map(([k, v]) => (
                    <DropdownMenuCheckboxItem
                      key={k}
                      checked={stateFilter.includes(k)}
                      onCheckedChange={(on) => {
                        setStateFilter((prev) => (on
                          ? [...prev, k]
                          : prev.filter((s) => s !== k)));
                      }}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`forecast-state-filter-${k}`}
                    >
                      {v}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Totals cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="forecast-totals">
        {monthsList.map((m) => (
          <Card key={m} data-testid={`forecast-total-${m}`}>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wide">{m}</p>
              <p className="text-lg font-bold text-slate-800 mt-1">{fmt(totalsByMonth[m])}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">Net after credits</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="forecast-anomalies">
          <ShieldAlert className="h-4 w-4 text-amber-700" />
          <AlertTitle className="text-amber-800">
            {anomalies.length} data anomal{anomalies.length === 1 ? 'y' : 'ies'} detected
          </AlertTitle>
          <AlertDescription className="text-amber-800 text-xs">
            <details>
              <summary className="cursor-pointer">Show details</summary>
              <div className="mt-3 space-y-2">
                {anomalies.slice(0, 25).map((a, i) => (
                  <div
                    key={i}
                    className="rounded border border-amber-200 bg-white/70 px-3 py-2"
                    data-testid={`forecast-anomaly-${i}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="border-amber-300 text-amber-800 text-[10px]">
                        {a.type?.replaceAll('_', ' ') || 'anomaly'}
                      </Badge>
                      {a.parent_name && (
                        <span className="font-semibold text-slate-800">{a.parent_name}</span>
                      )}
                      {a.parent_id && (
                        <a
                          href={`/admin/parents/${a.parent_id}`}
                          className="text-sky-700 underline text-[11px] font-mono"
                          data-testid={`forecast-anomaly-parent-link-${i}`}
                        >
                          view profile
                        </a>
                      )}
                      {a.difference != null && (
                        <span className="text-[11px] text-slate-600">
                          Δ {fmt(a.difference)}
                          {a.customer_credits_balance != null && a.credit_ledger_sum != null && (
                            <> · stored {fmt(a.customer_credits_balance)} vs ledger {fmt(a.credit_ledger_sum)}</>
                          )}
                        </span>
                      )}
                      {a.next_billing_date && (
                        <span className="text-[11px] text-slate-600">NBD {a.next_billing_date}</span>
                      )}
                      {/* iter328 — Inspect action only when this is a
                          credit-divergence row (has diff data). Opens
                          the modal that fetches the user's ledger
                          history so admins can VERIFY before choosing
                          a resolution direction. */}
                      {a.parent_id && a.customer_credits_balance != null && a.credit_ledger_sum != null && (
                        <button
                          type="button"
                          onClick={() => openInspect(a)}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded px-2 py-0.5"
                          data-testid={`forecast-anomaly-inspect-${i}`}
                        >
                          <Search className="h-3 w-3" />
                          Inspect
                        </button>
                      )}
                    </div>
                    {(a.player_name || a.program_name || a.parent_email) && (
                      <div className="mt-1 text-[11px] text-slate-500">
                        {a.player_name && <>Player: {a.player_name} · </>}
                        {a.program_name && <>Program: {a.program_name} · </>}
                        {a.parent_email && <>{a.parent_email}</>}
                      </div>
                    )}
                    {a.explanation && (
                      <div className="mt-1 text-[11px] text-amber-900">{a.explanation}</div>
                    )}
                  </div>
                ))}
                {anomalies.length > 25 && (
                  <div className="text-[11px] text-amber-700">
                    …and {anomalies.length - 25} more
                  </div>
                )}
              </div>
            </details>
          </AlertDescription>
        </Alert>
      )}

      {/* Table */}
      <Card>
        <CardContent className="pt-5">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading forecast…
            </div>
          ) : error ? (
            <div className="text-rose-700 text-sm py-6" data-testid="forecast-error">{error}</div>
          ) : matrix.length === 0 ? (
            <div className="text-slate-500 text-sm py-6" data-testid="forecast-empty">
              No enrollments match this projection window.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="forecast-table-wrap">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{renderSortHeader('parent_name', 'Parent', 'sort-parent')}</TableHead>
                    <TableHead>{renderSortHeader('player_name', 'Player', 'sort-player')}</TableHead>
                    <TableHead>{renderSortHeader('program_name', 'Program', 'sort-program')}</TableHead>
                    <TableHead>{renderSortHeader('payment_frequency', 'Freq', 'sort-freq')}</TableHead>
                    <TableHead>{renderSortHeader('next_billing_date', 'NBD', 'sort-nbd')}</TableHead>
                    {/* iter333g — enrollment_id column so admins (and
                        support agents) can pinpoint exactly which
                        record is leaking through the forecast filter. */}
                    <TableHead className="text-xs">Enrollment</TableHead>
                    {monthsList.map((m) => (
                      <TableHead key={m} className="text-right">{m}</TableHead>
                    ))}
                    <TableHead className="text-right">
                      {renderSortHeader('total', 'Total', 'sort-total')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {matrix.map((r) => (
                    <TableRow key={r.enrollment_id} data-testid={`forecast-row-${r.enrollment_id}`}>
                      <TableCell className="whitespace-nowrap">
                        <div className="font-medium text-slate-800">{r.parent_name || '—'}</div>
                        <div className="text-[11px] text-slate-500">{r.parent_email}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">{r.player_name || '—'}</TableCell>
                      <TableCell className="whitespace-nowrap text-slate-700">
                        <div>{r.program_name || '—'}</div>
                        <div className="text-[11px] text-slate-500">{r.class_name}</div>
                      </TableCell>
                      <TableCell><Badge variant="outline">{r.payment_frequency || 'monthly'}</Badge></TableCell>
                      <TableCell className="text-slate-600 text-xs">{r.next_billing_date || '—'}</TableCell>
                      {/* iter333g/h — clickable enrollment id. Opens
                          the parent's Admin detail in a new tab so
                          admins can inspect the exact enrollment
                          record. Falls back to non-link display
                          when parent_id isn't on the row. */}
                      <TableCell
                        className="text-[11px] font-mono text-slate-500 whitespace-nowrap"
                        data-testid={`forecast-enr-id-${r.enrollment_id}`}
                        title={r.enrollment_id}
                      >
                        {r.parent_id ? (
                          <a
                            href={`/admin/parents/${r.parent_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-blue-600 hover:underline"
                          >
                            {(r.enrollment_id || '').slice(0, 8)}
                          </a>
                        ) : (
                          (r.enrollment_id || '').slice(0, 8)
                        )}
                      </TableCell>
                      {monthsList.map((m) => {
                        const cell = r.months[m];
                        if (!cell) return <TableCell key={m} className="text-right text-slate-400">—</TableCell>;
                        // iter333h — For SCHEDULED_CANCEL cells the
                        // backend's net_after_credit correctly shows
                        // $0 (no new billing), but the owner wants
                        // to see the paid/contract amount alongside
                        // the Cancelled tag ("$325 Cancelled", not
                        // "$0 Cancelled"). Prefer cycle_amount when
                        // the row is in scheduled_cancel state.
                        const displayAmount = (cell.state === 'scheduled_cancel')
                          ? cell.cycle_amount
                          : cell.net_after_credit;
                        return (
                          <TableCell key={m} className="text-right whitespace-nowrap" data-testid={`cell-${r.enrollment_id}-${m}`}>
                            <div className="font-semibold text-slate-800">{fmt(displayAmount)}</div>
                            <div className="flex items-center justify-end gap-1 mt-0.5">
                              <StateBadge cell={cell} />
                              {cell.credit_applied > 0 && (
                                <span className="text-[9px] text-emerald-700" title={`Credit applied ${fmt(cell.credit_applied)}`}>
                                  −{fmt(cell.credit_applied)}
                                </span>
                              )}
                              {cell.cycle_amount !== cell.net_billable && cell.cycle_amount > 0 && cell.state !== 'active' && cell.state !== 'scheduled_cancel' && (
                                <span className="text-[9px] text-slate-400 line-through">
                                  {fmt(cell.cycle_amount)}
                                </span>
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right font-bold text-slate-900">{fmt(r.total)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-50 font-semibold" data-testid="forecast-footer-total">
                    <TableCell colSpan={5} className="text-right text-slate-700">Totals</TableCell>
                    {monthsList.map((m) => (
                      <TableCell key={m} className="text-right text-slate-900">{fmt(totalsByMonth[m])}</TableCell>
                    ))}
                    <TableCell className="text-right text-slate-900">{fmt(totalNet)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">How to read this</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-600">
            <div><Badge variant="outline" className={STATE_TONE.active}>Active</Badge> — cycle bills this month at the shown amount</div>
            <div><Badge variant="outline" className={STATE_TONE.paused}>Paused</Badge> — no charge until resume</div>
            <div><Badge variant="outline" className={STATE_TONE.scheduled_cancel}>Cancelled</Badge> — last billed cycle before scheduled cancellation; future months are hidden</div>
            <div><Badge variant="outline" className={STATE_TONE.covered_by_invoice}>Paid</Badge> — this month's own invoice is already paid or in-flight</div>
            <div><Badge variant="outline" className={STATE_TONE.cycle_not_due_this_month}>Cycle covers</Badge> — quarterly/biannual charge already billed at cycle-start month</div>
            <div><Badge variant="outline" className={STATE_TONE.not_yet_started}>Not started</Badge> — enrollment start_date is later than this month</div>
          </div>
        </CardContent>
      </Card>

      {/* iter328 — Credit-divergence Inspect + Resolve modal.
          Fetches the user's full credit_ledger history so admins can
          VERIFY each anomaly before picking a resolution direction. */}
      <Dialog open={inspectOpen} onOpenChange={setInspectOpen}>
        <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Credit divergence: {inspectAnomaly?.parent_name || inspectAnomaly?.parent_id}
            </DialogTitle>
          </DialogHeader>
          {inspectLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-500">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Loading ledger history…
            </div>
          ) : inspectData ? (
            <div className="space-y-4">
              {/* Divergence summary */}
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="p-3 bg-slate-50 rounded">
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Stored balance</div>
                  <div className="font-mono text-slate-900 text-lg">{fmt(inspectData.stored_balance)}</div>
                </div>
                <div className="p-3 bg-slate-50 rounded">
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Ledger sum</div>
                  <div className="font-mono text-slate-900 text-lg">{fmt(inspectData.ledger_sum)}</div>
                </div>
                <div className="p-3 bg-amber-50 rounded">
                  <div className="text-[10px] uppercase text-amber-700 font-semibold">Diff (stored − ledger)</div>
                  <div className={`font-mono text-lg ${inspectData.diff > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {inspectData.diff > 0 ? '+' : ''}{fmt(inspectData.diff)}
                  </div>
                </div>
              </div>

              {/* Ledger history */}
              <div>
                <div className="text-sm font-semibold text-slate-700 mb-2">
                  Ledger rows ({inspectData.ledger_row_count})
                </div>
                <div className="border rounded max-h-64 overflow-y-auto">
                  {inspectData.ledger_rows.length === 0 ? (
                    <div className="text-center py-6 text-sm text-slate-400">
                      No credit_ledger rows for this user.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-[10px]">Date</TableHead>
                          <TableHead className="text-[10px]">Kind</TableHead>
                          <TableHead className="text-[10px] text-right">Delta</TableHead>
                          <TableHead className="text-[10px]">Note</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inspectData.ledger_rows.map((row) => (
                          <TableRow key={row.id} data-testid={`ledger-row-${row.id}`}>
                            <TableCell className="text-[11px] text-slate-600 whitespace-nowrap">
                              {row.created_at?.slice(0, 10)}
                            </TableCell>
                            <TableCell className="text-[11px]">
                              <Badge variant="outline" className="text-[9px]">{row.kind || row.entity_type || '—'}</Badge>
                            </TableCell>
                            <TableCell className={`text-[11px] font-mono text-right ${(row.delta || 0) > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                              {(row.delta || 0) > 0 ? '+' : ''}{fmt(row.delta)}
                            </TableCell>
                            <TableCell className="text-[11px] text-slate-500 max-w-xs truncate" title={row.note}>
                              {row.note || '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </div>

              {/* Resolution */}
              <div className="border-t pt-3 space-y-2">
                <div className="text-sm font-semibold text-slate-700">Choose resolution</div>
                <RadioGroup value={resolveDirection} onValueChange={setResolveDirection}>
                  <label className="flex items-start gap-2 text-xs cursor-pointer p-2 hover:bg-slate-50 rounded">
                    <RadioGroupItem value="skip" id="rd-skip" className="mt-0.5" data-testid="resolve-direction-skip" />
                    <div>
                      <div className="font-semibold text-slate-800">Skip — record review, take no action</div>
                      <div className="text-slate-500">Use when the divergence is legitimate or needs more research.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 text-xs cursor-pointer p-2 hover:bg-slate-50 rounded">
                    <RadioGroupItem value="balance_wins" id="rd-balance" className="mt-0.5" data-testid="resolve-direction-balance" />
                    <div>
                      <div className="font-semibold text-slate-800">Balance is truth — write compensating ledger row</div>
                      <div className="text-slate-500">Use when the balance was manually corrected (e.g. zeroed) and the ledger needs to catch up.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 text-xs cursor-pointer p-2 hover:bg-slate-50 rounded">
                    <RadioGroupItem value="ledger_wins" id="rd-ledger" className="mt-0.5" data-testid="resolve-direction-ledger" />
                    <div>
                      <div className="font-semibold text-slate-800">Ledger is truth — update stored balance</div>
                      <div className="text-slate-500">Use when the ledger tells the true story and the balance drifted from manual updates.</div>
                    </div>
                  </label>
                </RadioGroup>

                {resolveDirection !== 'skip' && (
                  <div>
                    <label className="text-xs font-semibold text-slate-700 block mb-1">
                      Reason <span className="text-rose-600">*</span>
                    </label>
                    <Textarea
                      rows={2}
                      value={resolveReason}
                      onChange={(e) => setResolveReason(e.target.value)}
                      placeholder="e.g. Manual credit adjust on Feb 12 didn't journal; ledger drifted."
                      data-testid="resolve-reason-input"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 text-center py-6">No data</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInspectOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submitResolve}
              disabled={resolving || inspectLoading}
              data-testid="resolve-submit"
            >
              {resolving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {resolveDirection === 'skip' ? 'Acknowledge' : 'Apply resolution'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
